
import { useCallback, useRef, useMemo, useEffect } from 'react';
import { resolveOsbDims } from '../../utils/osbDims';
import { applicationMatchKeys, shareApplication } from '../../utils/insulationApplications';

const MAX_ANALOG_PRICE_AGE_DAYS = 20;

const IZOLIFE_SOURCE_PREFIX = 'ПРАЙС ИЗОЛАЙФ';

const isIzolifeSource = (source) =>
  source && String(source).toUpperCase().includes(IZOLIFE_SOURCE_PREFIX.toUpperCase());

const extractRegionFromIzolifeSource = (source) => {
  const upper = String(source || '').toUpperCase();
  if (upper.includes('СПБ')) return 'СПБ';
  if (upper.includes('МСК')) return 'МСК';
  if (upper.includes('КРД')) return 'КРД';
  return null;
};

const parseNum = (v) => {
  const n = parseFloat(String(v || '0').replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

/**
 * Считает цену за упаковку с учётом доставки для аналога из Изолайфа.
 * Возвращает объект с pricePerPackage, actualPackages, comment, phrase.
 */
const calcIzolifePrice = (data, name, requiredPackages, priceValue, cache) => {
  const source = data.SOURCE;
  const region = extractRegionFromIzolifeSource(source);

  const izoSettings = cache?.izolifeSettings?.settings?.find(s => s.REGION === region) || {};
  const palletPrice = parseNum(izoSettings.PALLET_PRICE);
  const phrase = izoSettings.PHRASE || '';

  const deliveryCostData = cache?.izolifeSettings?.deliveryCostByRegion?.[region] || {};
  const costPerTrip = parseNum(deliveryCostData.PODACHA);

  const numberPerPallet = parseNum(data.NUMBER_PER_PALLET) || 1;
  const truckPallets = parseNum(cache?.izolifeSettings?.truckPalletsByProduct?.[name]) || 0;

  const requiredPallets = Math.ceil(requiredPackages / numberPerPallet);

  let actualPallets, actualPackages, totalDeliveryCost, comment;

  if (!palletPrice) {
    // Нет цены кратной поддонам — добиваем до кратного фуре
    if (truckPallets > 0) {
      actualPallets = Math.ceil(requiredPallets / truckPallets) * truckPallets;
    } else {
      actualPallets = requiredPallets;
    }
    actualPackages = actualPallets * numberPerPallet;
    const numTrips = truckPallets > 0 ? actualPallets / truckPallets : 1;
    totalDeliveryCost = numTrips * costPerTrip;
    comment = `Нужно ${requiredPackages} упаковок, но до полной погрузки берется ${actualPackages}`;
  } else {
    // Есть цена кратная поддонам — добиваем до полного поддона
    actualPallets = requiredPallets; // уже округлено вверх
    actualPackages = actualPallets * numberPerPallet;
    const numFullTrips = truckPallets > 0 ? Math.floor(requiredPallets / truckPallets) : 0;
    const remainingPallets = truckPallets > 0 ? requiredPallets % truckPallets : requiredPallets;
    const hasPartialTruck = remainingPallets > 0;
    totalDeliveryCost = numFullTrips * costPerTrip + (hasPartialTruck ? costPerTrip + palletPrice : 0);
    comment = `Нужно ${requiredPackages} упаковок, но для полного поддона нужно ${actualPackages}`;
  }

  const pricePerPackageWithDelivery =
    (priceValue * actualPackages + totalDeliveryCost) / actualPackages;

  return { pricePerPackageWithDelivery, actualPackages, comment, phrase, totalDeliveryCost };
};

const parsePriceUpdateDate = (dateValue) => {
  if (!dateValue || typeof dateValue !== 'string') return null;
  const trimmed = dateValue.trim();
  const match = trimmed.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isPriceFreshEnoughForAnalog = (dateValue, source) => {
  if (source !== "ПРАЙС ОС МСК") return true;

  const parsedDate = parsePriceUpdateDate(dateValue);
  if (!parsedDate) return true;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  parsedDate.setHours(0, 0, 0, 0);

  const diffMs = today.getTime() - parsedDate.getTime();
  if (diffMs < 0) return true;

  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  return diffDays <= MAX_ANALOG_PRICE_AGE_DAYS;
};

// useAnalogs предоставляет функцию для поиска аналогов материалов
export const useAnalogs = (selectedOffer, options, analogsData, paymentMethod = 'cash', cache) => {
  const analogsCache = useRef({});

  // Очистка кэша при изменении analogsData
  useEffect(() => {
    analogsCache.current = {};
  }, [analogsData]);

  // Мемоизируем зависимости
  const memoizedOptions = useMemo(() => options, [options]);
  const memoizedMaterials = useMemo(() => selectedOffer?.materials || [], [selectedOffer?.materials]);
  
  // Фильтруем analogsData, оставляя только материалы с категориями "Утеплитель" или "OSB"
  const filteredAnalogsData = useMemo(() => {
    console.log('Filtering analogsData, total materials:', Object.keys(analogsData).length);
    const filtered = Object.fromEntries(
      Object.entries(analogsData).filter(([_, manufacturers]) =>
        Object.values(manufacturers).some(data => 
          (data.CATEGORY?.toLowerCase().includes("утеплитель") ||
          data.CATEGORY?.toLowerCase().includes("osb")) &&
          isPriceFreshEnoughForAnalog(data.PRICE_UPDATE_DATE, data.SOURCE)
        )
      )
    );
    console.log('Filtered analogsData, remaining materials:', Object.keys(filtered).length);
    return filtered;
  }, [analogsData]);

  // Функция для получения аналогов материала
  const getAnalogs = useCallback(async (materialName = selectedOffer?.materials[0]?.name, productQuantity) => {
    console.log('getAnalogs called with:', { materialName, productQuantity });
    const parsedQuantity = productQuantity || 1;
    const cacheKey = `${materialName}_${parsedQuantity}_${paymentMethod}`;
    
    if (analogsCache.current[cacheKey]) {
      console.log('Returning cached analogs for:', cacheKey);
      return analogsCache.current[cacheKey];
    }

    if (!materialName) {
      console.log('No material name provided');
      return [];
    }

    if (!Object.keys(filteredAnalogsData).length) {
      console.log('No filtered analogs data available');
      return [];
    }

    // Нормализация строк
    const normalizeString = (str) => str ? str.trim().toLowerCase().replace(/\s+/g, ' ').replace(/[^a-zа-я0-9]/g, '') : '';

    // Находим текущий материал
    // console.log('Available materials in filteredAnalogsData:', Object.keys(filteredAnalogsData));
    let matchedMaterialEntry = Object.entries(filteredAnalogsData[materialName] || {}).find(([manufacturer]) => {
      const normalizedManufacturer = normalizeString(manufacturer);
      const normalizedProduction = normalizeString(selectedOffer?.production || '');
      return normalizedManufacturer === normalizedProduction;
    });

    // Резервный поиск, если точное совпадение не найдено
    if (!matchedMaterialEntry) {
      console.warn('No exact match for material with production:', materialName, 'with production:', selectedOffer?.production, 'Available manufacturers:', Object.keys(filteredAnalogsData[materialName] || {}));
      matchedMaterialEntry = Object.entries(filteredAnalogsData[materialName] || {})[0];
      if (!matchedMaterialEntry) {
        console.log('No matched material at all for:', materialName);
        return [];
      }
      console.warn('Using fallback material:', matchedMaterialEntry[0], 'instead of:', selectedOffer?.production);
    }

    const [currentManufacturer, currentData] = matchedMaterialEntry;
    console.log('Matched material:', materialName, 'Manufacturer:', currentManufacturer, 'Data:', currentData);

    // Преобразование числовых полей текущего материала
    const currentDensity = currentData.DENSITY ? parseFloat(String(currentData.DENSITY).replace(",", ".")) || 0 : 0;
    const currentInsulationThickness = currentData.INSULATION_THICKNESS ? parseFloat(String(currentData.INSULATION_THICKNESS).replace(",", ".")) || 0 : 0;
    const currentInsulationThicknessOSB = currentData.OSB_THICKNESS_MM ? parseFloat(String(currentData.OSB_THICKNESS_MM).replace(",", ".")) || 0 : 0;
    const currentCategory = currentData.CATEGORY || "-";
    // Применения сравниваем по справочнику, а не строкой: одно и то же
    // применение написано в колонке по-разному, и впритык они не сходятся.
    const currentAnalogy = currentData.ANALOGY;
    const currentApplications = applicationMatchKeys(currentAnalogy);
    const currentSheetsInsulation = currentData.SHEETS_INSULATION ? parseInt(String(currentData.SHEETS_INSULATION).replace(",", ".")) || 0 : 0;
    const currentArea = currentData.AREA_SQUARE ? parseFloat(String(currentData.AREA_SQUARE).replace(",", ".")) || 0 : 0;
    const currentAreaOSB = currentData.AREA_OSB ? parseFloat(String(currentData.AREA_OSB).replace(",", ".")) || 0 : 0;
    const currentCompression = currentData.COMPRESSION ? parseFloat(String(currentData.COMPRESSION).replace(",", ".")) || 0 : 0;
    const currentPrice = paymentMethod === 'cash' ? currentData.PRICE_CASH : currentData.PRICE_NON_CASH || selectedOffer?.materials[0]?.pricePerOne || "0";
    const currentPriceValue = currentPrice ? parseFloat(String(currentPrice).replace(",", ".")) || 0 : 0;

    console.log('Selected material:', materialName, 'Manufacturer:', currentManufacturer, 'Price:', currentPriceValue, 'Density:', currentDensity, 'Area:', currentArea, 'AreaOSB:', currentAreaOSB, 'Analogy:', currentApplications);

    if (!currentPriceValue || currentPriceValue <= 0) {
      console.log('Invalid price for:', materialName, 'Price data:', currentData);
      return [];
    }

    const product = memoizedMaterials.find(m => normalizeString(m.name) === normalizeString(materialName));
    const quantity = product?.quantity ? parseFloat(product.quantity) || 0 : parsedQuantity;

    const isOsb = currentCategory.toLowerCase().includes("osb");
    const isUteplitel = currentCategory.toLowerCase().includes("утеплитель") && materialName.toLowerCase().includes("утеплитель");
    const isExtrudedPolystyrene = materialName.toLowerCase().includes("экструдированный пенополистирол");

    // KAL-43: для ОСБ добираем толщину/площадь из названия, если в справочнике пусто.
    const currentOsbDims = isOsb ? resolveOsbDims(materialName, currentData) : { thickness: 0, area: 0 };
    const resolvedThicknessOSB = isOsb ? currentOsbDims.thickness : currentInsulationThicknessOSB;
    const resolvedAreaOSB = isOsb ? currentOsbDims.area : currentAreaOSB;

    const effectiveArea = isOsb ? resolvedAreaOSB : currentArea;
    if (effectiveArea <= 0) {
      console.warn('Effective area is zero or negative for:', materialName, 'Data:', currentData);
      return [];
    }
    const effectiveThickness = isOsb ? resolvedThicknessOSB : currentInsulationThickness;
    const totalRequiredArea = quantity > 0 ? quantity * effectiveArea : effectiveArea;

    if (isUteplitel && (!currentInsulationThickness || !currentArea)) {
      console.log('Missing insulation thickness or area for:', materialName, 'Data:', currentData);
      return [];
    }
    if (isOsb && (!resolvedThicknessOSB || !resolvedAreaOSB)) {
      console.log('Missing OSB thickness or area for:', materialName, 'Data:', currentData);
      return [];
    }

    const currentItem = {
      value: materialName,
      label: materialName,
      details: {
        price: currentPrice || "Не указана",
        area: effectiveArea || "Нет данных",
        insulationThickness: effectiveThickness.toFixed(2) || "0.00",
        category: currentCategory || "-",
        sheetsInsulation: isOsb ? quantity : currentSheetsInsulation || "Нет данных",
        manufacturer: currentManufacturer,
        compression: isExtrudedPolystyrene ? currentCompression : undefined,
        density: (isUteplitel || isExtrudedPolystyrene) ? currentDensity : undefined,
      },
      requiredVolume: totalRequiredArea,
      pricePerPackage: currentPriceValue,
      pricePerSquareMeter: effectiveArea > 0 ? currentPriceValue / effectiveArea : Infinity,
      pricePerSheet: isOsb ? currentPriceValue : (currentSheetsInsulation > 0 ? currentPriceValue / currentSheetsInsulation : Infinity),
      volumePerOne: currentData.VOLUME_CUBIC ? parseFloat(String(currentData.VOLUME_CUBIC).replace(",", ".")) || 0 : 0,
      massPerOne: currentDensity > 0 && currentData.VOLUME_CUBIC ? currentDensity * parseFloat(String(currentData.VOLUME_CUBIC).replace(",", ".")) || 0 : 0,
    };

    // Определяем диапазон плотности для утеплителей
    let densityRange = 7; // Значение по умолчанию, если cache.densityRanges пуст или не найден
    if (isUteplitel && !isExtrudedPolystyrene && cache?.densityRanges?.length > 0) {
      // Находим подходящий диапазон из cache.densityRanges
      const densityRangeData = cache.densityRanges.find(range =>
        currentDensity >= range.densityFrom &&
        (range.densityTo === null || currentDensity <= range.densityTo)
      );
      densityRange = densityRangeData ? densityRangeData.range : 7;
    }
    

    let analogs = [];

    // Поиск аналогов среди всех материалов (не только текущей номенклатуры)
    const materialEntries = Object.entries(filteredAnalogsData);
    const chunkSize = 500; // Обрабатываем по 500 материалов за раз
    for (let i = 0; i < materialEntries.length; i += chunkSize) {
      await new Promise(resolve => setTimeout(resolve, 0)); // Даём UI обновиться
      const chunk = materialEntries.slice(i, i + chunkSize).flatMap(([name, manufacturers]) => {
        if (normalizeString(name) === normalizeString(materialName)) return [];
        return Object.entries(manufacturers).map(([manufacturer, data]) => ({ name, manufacturer, data }));
      });

      analogs.push(...chunk.filter(({ name, manufacturer, data }) => {
        if (!isPriceFreshEnoughForAnalog(data.PRICE_UPDATE_DATE, data.SOURCE)) {
          return false;
        }

        // KAL-43: для ОСБ толщину/площадь резолвим ниже через resolveOsbDims (справочник или название).
        const insulationThickness = data.INSULATION_THICKNESS ? parseFloat(String(data.INSULATION_THICKNESS).replace(",", ".")) || 0 : 0;
        const category = data.CATEGORY || "-";
        const areaSquare = data.AREA_SQUARE ? parseFloat(String(data.AREA_SQUARE).replace(",", ".")) || 0 : 0;
        const price = paymentMethod === 'cash' ? data.PRICE_CASH : data.PRICE_NON_CASH;
        const priceValue = price ? parseFloat(String(price).replace(",", ".")) || 0 : 0;
        const density = data.DENSITY ? parseFloat(String(data.DENSITY).replace(",", ".")) || 0 : 0;
        const compression = data.COMPRESSION ? parseFloat(String(data.COMPRESSION).replace(",", ".")) || 0 : 0;

        // Проверка совпадения хотя бы одного применения только для утеплителей
        const hasMatchingAnalogy = shareApplication(currentAnalogy, data.ANALOGY);

        // Проверка условий для OSB
        if (isOsb) {
          const { thickness: candThicknessOSB, area: candAreaOSB } = resolveOsbDims(name, data);
          const isSameInsulationThickness = candThicknessOSB > 0 && Math.abs(candThicknessOSB - resolvedThicknessOSB) < 1;
          const isOsbCategory = category.toLowerCase().includes("osb");
          return priceValue > 0 && candAreaOSB > 0 && isSameInsulationThickness && isOsbCategory;
        }
        // Проверка условий для экструдированного пенополистирола
        else if (isExtrudedPolystyrene) {
          const isSameInsulationThickness = insulationThickness > 0 && Math.abs(insulationThickness - currentInsulationThickness) < 1;
          const isUteplitelCategory = category.toLowerCase().includes("утеплитель") || category.toLowerCase().includes("экструдированный") || category.toLowerCase().includes("пенополистирол");
          
          // Проверка диапазона compression на основе данных из БД
          let isCompressionInRange = compression >= (currentCompression - 25); // fallback значение
          if (cache?.compressionRanges?.length > 0) {
            // Ищем подходящий диапазон в БД
            const compressionRange = cache.compressionRanges.find(range => {
              const fromValue = range.fromValue;
              const toValue = range.toValue;
              const compressionDiff = compression - currentCompression;
              
              if (toValue === null || toValue === '') {
                // Если верхняя граница null, проверяем только нижнюю
                return compressionDiff >= fromValue;
              } else {
                // Если есть обе границы, проверяем диапазон
                return compressionDiff >= fromValue && compressionDiff <= toValue;
              }
            });
            
            // Если нашли подходящий диапазон, значит compression в допустимых пределах
            isCompressionInRange = compressionRange !== undefined;
          }
          return (
            priceValue > 0 &&
            name.toLowerCase().includes("экструдированный пенополистирол") &&
            isSameInsulationThickness &&
            isUteplitelCategory &&
            isCompressionInRange
          );
        }
        // Проверка условий для утеплителя
        else if (isUteplitel) {
          const isSameInsulationThickness = insulationThickness > 0 && Math.abs(insulationThickness - currentInsulationThickness) < 1;
          const isUteplitelCategory = category.toLowerCase().includes("утеплитель") || category.toLowerCase().includes("экструдированный") || category.toLowerCase().includes("пенополистирол");
          const isDensityWithinRange = density >= currentDensity - densityRange && density <= currentDensity + densityRange;
          const isBaseCondition = priceValue > 0 &&
            name.toLowerCase().includes("утеплитель") &&
            !name.toLowerCase().includes("экструдированный пенополистирол") &&
            isSameInsulationThickness &&
            isUteplitelCategory;

          const analogTypeMaterial = data.TYPE_MATERIAL || null;
          if (analogTypeMaterial === "стекловолокно") {
            // Стекловолокно: только по применениям, толщина не важна
            return priceValue > 0 &&
              name.toLowerCase().includes("утеплитель") &&
              !name.toLowerCase().includes("экструдированный пенополистирол") &&
              isUteplitelCategory &&
              hasMatchingAnalogy;
          }
          // Базальтовая вата и null — старые правила (плотность + толщина + применения)
          return (
            isBaseCondition &&
            isDensityWithinRange &&
            (currentApplications.length === 0 || hasMatchingAnalogy)
          );
        }
        return false;
      }).map(({ name, manufacturer, data }) => {
        // KAL-43: для ОСБ площадь/толщина — из справочника или, если пусто, из названия.
        const osbDims = isOsb ? resolveOsbDims(name, data) : null;
        const area = isOsb ? osbDims.area : data.AREA_SQUARE;
        const insulationThickness = isOsb ? osbDims.thickness : data.INSULATION_THICKNESS;
        const sheetsInsulation = data.SHEETS_INSULATION ? parseInt(String(data.SHEETS_INSULATION).replace(",", ".")) || 0 : 0;
        const price = paymentMethod === 'cash' ? data.PRICE_CASH : data.PRICE_NON_CASH;
        const priceValue = price ? parseFloat(String(price).replace(",", ".")) || 0 : 0;
        const volumePerOne = data.VOLUME_CUBIC ? parseFloat(String(data.VOLUME_CUBIC).replace(",", ".")) || 0 : 0;
        const density = data.DENSITY ? parseFloat(String(data.DENSITY).replace(",", ".")) || 0 : 0;
        const massPerOne = density > 0 && volumePerOne ? density * volumePerOne / 1000 : 0;
        const compression = data.COMPRESSION ? parseFloat(String(data.COMPRESSION).replace(",", ".")) || 0 : 0;

        if (!area || area <= 0) {
          console.log('Invalid area for analog:', name, 'Manufacturer:', manufacturer, 'Area:', area);
          return null;
        }

        const requiredPackages = Math.ceil(totalRequiredArea / area);

        // --- Изолайф: расчёт с учётом доставки ---
        if (isIzolifeSource(data.SOURCE)) {
          const { pricePerPackageWithDelivery, actualPackages, comment, phrase, totalDeliveryCost } =
            calcIzolifePrice(data, name, requiredPackages, priceValue, cache);

          const analogPricePerSquareMeter = pricePerPackageWithDelivery / area;
          const analogPricePerPackage = pricePerPackageWithDelivery;
          const analogPricePerSheet = sheetsInsulation > 0 ? pricePerPackageWithDelivery / sheetsInsulation : Infinity;
          const priceDifferencePerSquareMeter = analogPricePerSquareMeter - currentItem.pricePerSquareMeter;
          const priceDifferencePerPackage = analogPricePerPackage - currentItem.pricePerPackage;
          const priceDifferencePerSheet = analogPricePerSheet - currentItem.pricePerSheet;
          const analogSquareMeters = analogPricePerSquareMeter * (actualPackages * area);
          const currentSquareMetersCost = currentItem.pricePerSquareMeter * totalRequiredArea;
          const volumeBenefit = analogSquareMeters - currentSquareMetersCost;

          return {
            value: name,
            label: name,
            details: {
              price: price || "Не указана",
              area: area || "Нет данных",
              insulationThickness: insulationThickness || "0.00",
              category: data.CATEGORY || "-",
              sheetsInsulation: sheetsInsulation || "Нет данных",
              manufacturer: manufacturer,
              compression: isExtrudedPolystyrene ? compression : undefined,
              density: (isUteplitel || isExtrudedPolystyrene) ? density : undefined,
              typeMaterial: isUteplitel ? (data.TYPE_MATERIAL || null) : undefined,
            },
            requiredPackages: actualPackages,
            pricePerPackage: pricePerPackageWithDelivery,
            analogPricePerPackage,
            priceDifferencePerPackage,
            analogPricePerSquareMeter,
            priceDifferencePerSquareMeter,
            analogPricePerSheet,
            priceDifferencePerSheet,
            volumeBenefit,
            volumePerOne,
            massPerOne,
            isIzolife: true,
            izolifeComment: comment,
            izolifePhrase: phrase,
            deliveryCostIncluded: true,
            deliveryCostPerPackage: actualPackages > 0 ? totalDeliveryCost / actualPackages : 0,
          };
        }

        // --- Стандартный расчёт ---
        const analogPricePerSquareMeter = priceValue / area;
        const priceDifferencePerSquareMeter = analogPricePerSquareMeter - currentItem.pricePerSquareMeter;
        const analogPricePerPackage = priceValue;
        const priceDifferencePerPackage = analogPricePerPackage - currentItem.pricePerPackage;
        const analogPricePerSheet = sheetsInsulation > 0 ? priceValue / sheetsInsulation : Infinity;
        const priceDifferencePerSheet = analogPricePerSheet - currentItem.pricePerSheet;
        const analogSquareMeters = analogPricePerSquareMeter * (requiredPackages * area);
        const currentSquareMetersCost = currentItem.pricePerSquareMeter * totalRequiredArea;
        const volumeBenefit = analogSquareMeters - currentSquareMetersCost;

        return {
          value: name,
          label: name,
          details: {
            price: price || "Не указана",
            area: area || "Нет данных",
            insulationThickness: insulationThickness || "0.00",
            category: data.CATEGORY || "-",
            sheetsInsulation: sheetsInsulation || "Нет данных",
            manufacturer: manufacturer,
            compression: isExtrudedPolystyrene ? compression : undefined,
            density: (isUteplitel || isExtrudedPolystyrene) ? density : undefined,
            typeMaterial: isUteplitel ? (data.TYPE_MATERIAL || null) : undefined,
          },
          requiredPackages,
          pricePerPackage: priceValue,
          analogPricePerPackage: priceValue,
          priceDifferencePerPackage,
          analogPricePerSquareMeter,
          priceDifferencePerSquareMeter,
          analogPricePerSheet,
          priceDifferencePerSheet,
          volumeBenefit,
          volumePerOne,
          massPerOne,
        };
      }).filter(analog => analog !== null && analog.volumeBenefit < 0));

      if (analogs.length >= (isOsb || isExtrudedPolystyrene ? 3 : 6)) break; // Прерываем, если найдено достаточно аналогов
    }

    analogs = analogs
      .sort((a, b) => isOsb || isExtrudedPolystyrene ? a.analogPricePerSquareMeter - b.analogPricePerSquareMeter : a.volumeBenefit - b.volumeBenefit)
      .slice(0, isOsb || isExtrudedPolystyrene ? 3 : 6);

    const allItems = [currentItem, ...analogs];
    analogsCache.current[cacheKey] = allItems;
    console.log('Analogs found:', allItems);
    return allItems;
  }, [memoizedOptions, memoizedMaterials, selectedOffer, filteredAnalogsData, paymentMethod]);

  return { getAnalogs };
};
